import { BryntumGrid } from "@bryntum/schedulerpro-react"
import { useEffect } from "react"
import { createPortal } from "react-dom"

import useHasAccess from "@hooks/useHasAccess"

import { faIcon } from "@utils/faIcon"

import DrawerHeader from "@atoms/BottomDrawer/DrawerHeader/DrawerHeader"

import BottomSheet from "@organisms/BottomSheet/BottomSheet"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./JobTimelineView.module.scss"
import { gridConfig } from "./JobTimelineViewConfig/JobTimelineViewConfig"
import UnscheduledJobsBottomSheetTrigger from "./UnscheduledJobsBottomSheetTrigger/UnscheduledJobsBottomSheetTrigger"
import UnscheduledJobsColumnMessage from "./UnscheduledJobsColumnMessage/UnscheduledJobsColumnMessage"
import useJobTimelineViewBryntumInstances from "./hooks/useJobTimelineViewBryntumInstances"
import useJobTimelineViewData from "./hooks/useJobTimelineViewData"
import useJobTimelineViewGridProps from "./hooks/useJobTimelineViewGridProps"
import useJobTimelineViewStates from "./hooks/useJobTimelineViewStates"
import useTimelineViewUnscheduledJobsUtils from "./hooks/useTimelineViewUnscheduledJobsUtils"

const body = document.querySelector("body")

export default function JobTimelineViewMobileUnscheduledJobs() {
    const {
        unscheduledJobsColumnStatus,
        unscheduledJobsColumnMessageStatus,
        showUnscheduledJobsColumnMessage,
        hideUnscheduledJobsColumnMessage,
        collapseUnscheduledJobsColumn,
        expandUnscheduledJobsColumn,
    } = useJobTimelineViewStates()

    const { hasPermission } = useHasAccess()

    const { manageUnscheduledJobsProcess, initializeUnscheduledJobsGrid } = useTimelineViewUnscheduledJobsUtils()

    const { mobileProps } = useJobTimelineViewGridProps()

    const { unscheduledJobsGrid, schedulerPro } = useJobTimelineViewBryntumInstances()

    const { unscheduledJobsData, unscheduledJobsCount } = useJobTimelineViewData()

    useEffect(() => {
        const hasUnscheduledJobsData = unscheduledJobsData !== undefined && unscheduledJobsData?.pages?.length > 0
        if (hasUnscheduledJobsData) {
            manageUnscheduledJobsProcess()
        }
    }, [unscheduledJobsData?.pages])

    useEffect(() => {
        // Initializes the drag and drop functionality for the Grid -> Scheduler.
        const scheduler = schedulerPro.current?.instance

        if (scheduler) {
            initializeUnscheduledJobsGrid()
        }
    }, [schedulerPro.current?.instance])

    return (
        <>
            {hasPermission("jobs_edit_permission", PERMISSION_LEVEL.FULL)
                ? body &&
                  createPortal(
                      <UnscheduledJobsBottomSheetTrigger
                          count={unscheduledJobsCount}
                          onClick={expandUnscheduledJobsColumn}
                      />,
                      body,
                  )
                : null}
            <BottomSheet.Root
                isOpen={
                    unscheduledJobsColumnStatus === "expanded" &&
                    hasPermission("jobs_edit_permission", PERMISSION_LEVEL.FULL)
                }
                onClose={collapseUnscheduledJobsColumn}
                className="unscheduled-jobs-grid__bottom-sheet"
                isFullHeight={true}
            >
                {unscheduledJobsColumnMessageStatus === "initial" && (
                    <div className={styles.unscheduledJobsMessage}>
                        <UnscheduledJobsColumnMessage
                            showButton={true}
                            onButtonClick={hideUnscheduledJobsColumnMessage}
                        />
                    </div>
                )}

                <DrawerHeader.Root>
                    <DrawerHeader.Title title={`${unscheduledJobsCount} Unscheduled Jobs`} />
                    <DrawerHeader.RightButton
                        aria-label="Unscheduled Jobs Help"
                        icon={faIcon({ name: "circle-question", style: "regular" })}
                        iconOnActive={faIcon({ name: "circle-question", style: "solid" })}
                        onClick={showUnscheduledJobsColumnMessage}
                        isPressed={unscheduledJobsColumnMessageStatus === "shown"}
                    />
                </DrawerHeader.Root>
                <BryntumGrid {...gridConfig} ref={unscheduledJobsGrid} {...mobileProps} />
            </BottomSheet.Root>

            <BottomSheet.Root
                isOpen={unscheduledJobsColumnStatus === "expanded" && unscheduledJobsColumnMessageStatus === "shown"}
                onClose={hideUnscheduledJobsColumnMessage}
            >
                <BottomSheet.DragIndicator />
                <UnscheduledJobsColumnMessage showBorderTop={false} />
            </BottomSheet.Root>
        </>
    )
}
